import React, { lazy, Suspense, useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { TopBar } from './TopBar';
import { PortalNotificationBanner } from './PortalNotificationBanner';
import { TOP_BAR_HEIGHT } from './navConfig';
import { CWYLoader } from '../common/CWYLoader';
import AgentPhoneDock from '../agent-phone/AgentPhoneDock';
import { FloatingAdminAssistant } from '../../features/ai-knowledge/components/FloatingAdminAssistant';

import AgentPhoneScreenPopListener from '../agent-phone/AgentPhoneScreenPopListener';
import { CommunityProvider } from '../../features/community/contexts/CommunityContext';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useBarcodeScanner } from '../../shared/hooks/useBarcodeScanner';
const QrCheckInDialog = lazy(() =>
  import('../../features/walk-ins/components/QrCheckInDialog').then((m) => ({ default: m.QrCheckInDialog }))
);

const LayoutContent = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isCommunityRoute =
    location.pathname === '/app/community/join' ||
    location.pathname.startsWith('/app/community/join/');
  const isAiAssistantRoute = location.pathname === '/app/ai-assistant';
  const mainShellBg = '#f5f5f5';

  // Walk-in scan check-in dialog state
  const [scannedQrCode, setScannedQrCode] = useState<string | null>(null);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState<boolean>(false);

  const hasWalkInAccess = Boolean(
    user?.pageAccess?.includes('/app/walk-ins') ||
    user?.permissions?.includes('all.read') ||
    user?.permissionRole === 'admin' ||
    user?.permissionRole === 'superadmin' ||
    (user as any)?.isReceptionist
  );

  const handleScan = useCallback((code: string) => {
    setScannedQrCode(code);
    setIsQrDialogOpen(true);
  }, []);

  const walkInsListOpen = location.pathname === '/app/walk-ins';

  useBarcodeScanner({
    enabled: hasWalkInAccess && !walkInsListOpen,
    onScan: handleScan,
  });

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: mainShellBg,
      minHeight: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      overflow: 'hidden',
      '@media print': {
        position: 'static',
        overflow: 'visible',
        minHeight: 'auto',
        height: 'auto',
        width: '100%',
        backgroundColor: '#fff',
      },
    }}>
      <TopBar />
      <PortalNotificationBanner />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: mainShellBg,
          p: isCommunityRoute ? 0 : { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 4 },
          overflow: isCommunityRoute || isAiAssistantRoute ? 'hidden' : 'auto',
          position: 'relative',
          width: '100%',
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          mt: `${TOP_BAR_HEIGHT}px`,
          display: isCommunityRoute || isAiAssistantRoute ? 'flex' : 'block',
          flexDirection: isCommunityRoute || isAiAssistantRoute ? 'column' : undefined,
          minHeight: 0,
          boxSizing: 'border-box',
          '@media print': {
            width: '100% !important',
            height: 'auto',
            minHeight: 'auto',
            mt: 0,
            p: 0,
            bgcolor: '#fff',
            overflow: 'visible',
          },
          ...(isCommunityRoute
            ? {
                '& > *': {
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  boxShadow: 'none',
                  p: 0,
                  flex: 1,
                  minHeight: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                },
              }
            : isAiAssistantRoute
              ? {
                  '& > *': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    p: { xs: 2, sm: 2.5, md: 3 },
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    '@media print': {
                      borderRadius: 0,
                      boxShadow: 'none',
                      minHeight: 'auto',
                      p: 0,
                      backgroundColor: '#fff',
                    },
                  },
                }
              : {
                '& > *': {
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  p: { xs: 2, sm: 2.5, md: 3 },
                  minHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px - 48px)`,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  flex: '1 1 auto',
                  '@media print': {
                    borderRadius: 0,
                    boxShadow: 'none',
                    minHeight: 'auto',
                    p: 0,
                    backgroundColor: '#fff',
                  },
                },
              }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            ...(isCommunityRoute || isAiAssistantRoute ? {} : { overflowY: 'auto', overflowX: 'hidden' }),
          }}
        >
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  minHeight: 'min(480px, 70vh)',
                }}
              >
                <CWYLoader size={72} />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      <AgentPhoneScreenPopListener />
      <Box sx={{ '@media print': { display: 'none !important' } }}>
        <AgentPhoneDock />
      </Box>
      <Box sx={{ '@media print': { display: 'none !important' } }}>
        <FloatingAdminAssistant />
      </Box>
      {hasWalkInAccess && isQrDialogOpen ? (
        <Suspense fallback={null}>
          <QrCheckInDialog
            open
            scannedCode={scannedQrCode}
            onClose={() => {
              setIsQrDialogOpen(false);
              setScannedQrCode(null);
            }}
          />
        </Suspense>
      ) : null}
    </Box>
  );
};

export const Layout = () => {
  return (
    <CommunityProvider>
      <LayoutContent />
    </CommunityProvider>
  );
};
